import React from "react";
import {
  UseFormRegister,
  Control,
  Controller,
  FieldErrors,
} from "react-hook-form";
import {
  AlertCircle,
  Briefcase,
  DollarSign,
  Clock,
  Users,
  ChevronDown,
} from "lucide-react";
import ProjectFormSection from "./ProjectFormSection";
import { ProjectDifficulty } from "../../../lib/types/adminProjects";

interface ProjectFormDetailsProps {
  register: UseFormRegister<any>;
  control: Control<any>;
  errors: FieldErrors<any>;
  isActive: boolean;
  onToggle: () => void;
}

const DIFFICULTIES: ProjectDifficulty[] = ["Easy", "Medium", "Hard"];

const ESTIMATED_HOURS_OPTIONS = [
  "Less than 1 hour",
  "1-2 hours",
  "2-3 hours",
  "3-4 hours",
  "4-5 hours",
  "5+ hours",
];

const getDifficultyConfig = (difficulty: string) => {
  switch (difficulty) {
    case "Easy":
      return { color: "text-emerald-600", bg: "bg-emerald-100", bars: 1 };
    case "Medium":
      return { color: "text-amber-600", bg: "bg-amber-100", bars: 2 };
    case "Hard":
      return { color: "text-red-600", bg: "bg-red-100", bars: 3 };
    default:
      return { color: "text-gray-600", bg: "bg-gray-100", bars: 0 };
  }
};

const ProjectFormDetails: React.FC<ProjectFormDetailsProps> = ({
  register,
  control,
  errors,
  isActive,
  onToggle,
}) => {
  return (
    <ProjectFormSection
      id="details"
      title="Project Details"
      description="Rate, difficulty, and slots"
      icon={Briefcase}
      iconGradient="from-emerald-500 to-teal-500"
      isActive={isActive}
      onToggle={onToggle}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
        {/* Hourly Rate */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
            Hourly Rate ($) <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
            <input
              {...register("hourlyRate", { valueAsNumber: true })}
              type="number"
              min={1}
              max={500}
              placeholder="35"
              className={`w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2.5 sm:py-3 text-sm sm:text-base rounded-lg sm:rounded-xl border-2 transition-all focus:outline-none ${
                errors.hourlyRate
                  ? "border-red-300 focus:border-red-500 bg-red-50"
                  : "border-gray-200 focus:border-[#8B52FF] focus:ring-2 focus:ring-[#8B52FF]/20"
              }`}
            />
          </div>
          {errors.hourlyRate && (
            <p className="mt-1.5 text-xs sm:text-sm text-red-600 flex items-center gap-1">
              <AlertCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
              {errors.hourlyRate.message as string}
            </p>
          )}
        </div>

        {/* Total Slots */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
            Total Slots <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
            <input
              {...register("totalSlots", { valueAsNumber: true })}
              type="number"
              min={1}
              max={1000}
              placeholder="20"
              className={`w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2.5 sm:py-3 text-sm sm:text-base rounded-lg sm:rounded-xl border-2 transition-all focus:outline-none ${
                errors.totalSlots
                  ? "border-red-300 focus:border-red-500 bg-red-50"
                  : "border-gray-200 focus:border-[#8B52FF] focus:ring-2 focus:ring-[#8B52FF]/20"
              }`}
            />
          </div>
          {errors.totalSlots && (
            <p className="mt-1.5 text-xs sm:text-sm text-red-600 flex items-center gap-1">
              <AlertCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
              {errors.totalSlots.message as string}
            </p>
          )}
        </div>

        {/* Estimated Hours */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
            Estimated Hours <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
            <select
              {...register("estimatedHours")}
              className={`w-full pl-9 sm:pl-10 pr-8 sm:pr-10 py-2.5 sm:py-3 text-sm sm:text-base rounded-lg sm:rounded-xl border-2 transition-all focus:outline-none bg-white appearance-none ${
                errors.estimatedHours
                  ? "border-red-300 focus:border-red-500 bg-red-50"
                  : "border-gray-200 focus:border-[#8B52FF] focus:ring-2 focus:ring-[#8B52FF]/20"
              }`}
            >
              <option value="">Select estimated hours</option>
              {ESTIMATED_HOURS_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400 pointer-events-none" />
          </div>
          {errors.estimatedHours && (
            <p className="mt-1.5 text-xs sm:text-sm text-red-600 flex items-center gap-1">
              <AlertCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
              {errors.estimatedHours.message as string}
            </p>
          )}
        </div>

        {/* Difficulty */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
            Difficulty Level <span className="text-red-500">*</span>
          </label>
          <Controller
            name="difficulty"
            control={control}
            render={({ field }) => (
              <div className="flex gap-1.5 sm:gap-2">
                {DIFFICULTIES.map((difficulty) => {
                  const config = getDifficultyConfig(difficulty);
                  const isSelected = field.value === difficulty;
                  return (
                    <button
                      key={difficulty}
                      type="button"
                      onClick={() => field.onChange(difficulty)}
                      className={`flex-1 p-2 sm:p-3 rounded-lg sm:rounded-xl border-2 text-xs sm:text-sm font-medium transition-all flex items-center justify-center gap-1 sm:gap-2 ${
                        isSelected
                          ? `${config.bg} ${config.color} border-current`
                          : "border-gray-200 text-gray-600 hover:border-gray-300"
                      }`}
                    >
                      {difficulty}
                    </button>
                  );
                })}
              </div>
            )}
          />
          {errors.difficulty && (
            <p className="mt-1.5 text-xs sm:text-sm text-red-600 flex items-center gap-1">
              <AlertCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
              {errors.difficulty.message as string}
            </p>
          )}
        </div>
      </div>
    </ProjectFormSection>
  );
};

export default ProjectFormDetails;
export { getDifficultyConfig, DIFFICULTIES, ESTIMATED_HOURS_OPTIONS };
